import { useState } from 'react';

import { OrganizationCodes, Certificate, CardCodes, CARD_CODES } from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { thematicColors } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { useIsomorphicLayoutEffect } from '@revenuemarket/react';
import { Toast } from '@revenuemarket/ui';
import { Button, Menu } from 'antd';

import CertificateConnector from '~components/Certificate/tabs/CertificateConnector';
import { useCreateCertificates } from '~hooks/queries/certificates/query';
import useCertificatesQuery from '~hooks/queries/useCertificatesQuery';
import useGetCorporateCardConnections from '~hooks/queries/useCorporateCardConnections';
import useGetCorporateCardList from '~hooks/queries/useGetCorporateCardList';
import useCodef from '~hooks/useCodef';
import { useCompanyId } from '~hooks/useCompanyId';
import { CodefCertificateHardDiskPath } from '~models/certificate';

import AlertConnectInfo from './AlertConnectInfo';

interface Props {
  onSuccess: () => void;
  onLoginTypeChange: () => void;
  organizationCode: OrganizationCodes;
  initScrape: boolean;
  handleLogin?: (certificate: Certificate) => void;
}

export const CardLoginByCertificate: React.FC<Props> = ({
  onSuccess,
  onLoginTypeChange,
  organizationCode,
  initScrape,
}) => {
  const { companyId } = useCompanyId();
  const { requestExternalDrive } = useCodef();
  const [drivePaths, setDrivePaths] = useState<string[]>([]);
  const [drivePath, setDrivePath] = useState(CodefCertificateHardDiskPath);

  const { refetch: refetchCardList } = useGetCorporateCardList(companyId);
  const { refetch: refetchCardConnections } = useGetCorporateCardConnections(companyId as string);

  const { data: certificate } = useCertificatesQuery(companyId);
  const certificateList = certificate?.Data?.data?.certificates || [];

  useIsomorphicLayoutEffect(() => {
    (async () => {
      try {
        const drivePaths = await requestExternalDrive();
        setDrivePaths(drivePaths);
      } catch {}
    })();
  }, []);

  const { mutateAsync: createCertificates } = useCreateCertificates();

  const organizationName = CARD_CODES[organizationCode as CardCodes];
  return (
    <Flex direction="column" gap={24} justify="flex-start">
      <AlertConnectInfo
        organizationCode={organizationCode as CardCodes}
        onChangeOrganizationCode={onLoginTypeChange}
      />
      <Flex direction="column" gap={16}>
        <Menu
          mode="horizontal"
          selectedKeys={[drivePath]}
          onClick={(e) => {
            setDrivePath(e.key);
          }}
          items={[
            {
              label: '하드디스크',
              key: CodefCertificateHardDiskPath,
            },
            {
              label: '이동식디스크',
              key: 'submenu',
              children: drivePaths.map((path) => ({
                label: path,
                key: path,
              })),
            },
          ]}
        />
        <CertificateConnector
          drivePath={drivePath}
          registeredCertificates={certificateList}
          onConnect={async (certificate) => {
            const response = await createCertificates({
              companyId,
              request: {
                initScrape,
                certificates: [certificate],
              },
            });
            if (response.Status === ResponseStatus.SUCCESS) {
              Toast.success('공동인증서 등록이 완료되었습니다.');
              onSuccess();
              refetchCardList();
              refetchCardConnections();
            } else {
              if (response.ErrorCode === 'NO_LINKED_ORGANIZATION') {
                Toast.error('공동인증서 기반 연동할 수 있는 기관이 없습니다.');
                return;
              }
              Toast.error('공동인증서 등록에 실패했습니다.');
            }
          }}
        />
      </Flex>
      <Flex>
        <Button
          size="small"
          type="link"
          style={{
            width: 'fit-content',
            color: thematicColors.primary.medium,
          }}
          onClick={() => {
            onLoginTypeChange();
          }}
        >
          {`${organizationName} 연결 돌아가기`}
        </Button>
      </Flex>
    </Flex>
  );
};
